import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  ImageList,
  ImageListItem,
  TextField,
  MobileStepper,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Preview,
  Twitter,
  Facebook,
  Instagram,
  VideoLibrary,
  Refresh,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from '@mui/icons-material';
import { socialMediaApi } from '../../api/socialMediaApi';

interface Post {
  postId: string;
  platform: string;
  contentType: string;
  content?: {
    text?: string;
    caption?: string;
    images?: string[];
    videoUrl?: string;
  };
  postDate: string;
  createdAt: number;
  status: string;
  gameData?: any;
}

interface PendingPostsProps {
  onAlert: (type: 'success' | 'error' | 'info', message: string) => void;
}

const PendingPosts: React.FC<PendingPostsProps> = ({ onAlert }) => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    fetchPendingPosts();
  }, []);

  const fetchPendingPosts = async () => {
    try {
      setLoading(true);
      const response = await socialMediaApi.getPendingPosts();
      // The response is the array directly, not wrapped in a posts property
      const postsArray = Array.isArray(response) ? response : response?.posts || [];

      // Sort by createdAt ascending (oldest first)
      postsArray.sort((a: Post, b: Post) => a.createdAt - b.createdAt);

      setPosts(postsArray);
    } catch (error) {
      onAlert('error', 'Failed to fetch pending posts');
      console.error('Error fetching pending posts:', error);
      setPosts([]); // Ensure posts is always an array
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (postId: string) => {
    try {
      await socialMediaApi.approvePost(postId);
      onAlert('success', 'Post approved successfully');
      fetchPendingPosts(); // Refresh list
    } catch (error) {
      onAlert('error', 'Failed to approve post');
      console.error('Error approving post:', error);
    }
  };

  const handleReject = async () => {
    if (!selectedPost) return;

    try {
      await socialMediaApi.rejectPost(selectedPost.postId, rejectReason);
      onAlert('info', 'Post rejected');
      setRejectDialogOpen(false);
      setSelectedPost(null);
      setRejectReason('');
      fetchPendingPosts();
    } catch (error) {
      onAlert('error', 'Failed to reject post');
      console.error('Error rejecting post:', error);
    }
  };

  const handlePreview = (post: Post) => {
    setSelectedPost(post);
    setActiveStep(0);
    setPreviewOpen(true);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'twitter':
        return <Twitter sx={{ color: '#1DA1F2' }} />;
      case 'facebook':
        return <Facebook sx={{ color: '#1877F2' }} />;
      case 'instagram':
        return <Instagram sx={{ color: '#E4405F' }} />;
      case 'tiktok':
        return <VideoLibrary sx={{ color: '#000000' }} />;
      default:
        return null;
    }
  };

  const getContentTypeChip = (contentType: string) => {
    const colors: { [key: string]: 'primary' | 'secondary' | 'success' | 'warning' } = {
      'daily-reveal': 'primary',
      'carousel': 'secondary',
      'leaderboard': 'success',
      'story': 'warning',
    };

    return (
      <Chip
        label={contentType}
        color={colors[contentType] || 'default'}
        size="small"
        sx={{ ml: 1 }}
      />
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Pending Posts ({posts?.length || 0})
        </Typography>
        <Button
          startIcon={<Refresh />}
          onClick={fetchPendingPosts}
          variant="outlined"
        >
          Refresh
        </Button>
      </Box>

      {!posts || posts.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary" align="center">
              No posts pending approval
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date Created</TableCell>
                <TableCell>Platform</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Content</TableCell>
                <TableCell>Media</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {posts.map((post) => (
                <TableRow key={post.postId} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(post.createdAt).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(post.createdAt).toLocaleTimeString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      {getPlatformIcon(post.platform)}
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {post.platform}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {getContentTypeChip(post.contentType)}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {post.content?.text || post.content?.caption || 'No text content'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {post.content?.images && post.content?.images?.length > 0 && (
                      <Chip
                        size="small"
                        label={
                          post.contentType === 'carousel'
                            ? `${post.content?.images?.length} slides`
                            : `${post.content?.images?.length} image${post.content?.images?.length > 1 ? 's' : ''}`
                        }
                        color="info"
                        variant="outlined"
                      />
                    )}
                    {post.content?.videoUrl && (
                      <Chip
                        size="small"
                        label="Video"
                        color="info"
                        variant="outlined"
                      />
                    )}
                    {!post.content?.images && !post.content?.videoUrl && (
                      <Typography variant="caption" color="text.secondary">
                        None
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" gap={1} justifyContent="center">
                      <IconButton
                        size="small"
                        onClick={() => handlePreview(post)}
                        color="info"
                        title="Preview"
                      >
                        <Preview />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleApprove(post.postId)}
                        title="Approve"
                      >
                        <CheckCircle />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setSelectedPost(post);
                          setRejectDialogOpen(true);
                        }}
                        title="Reject"
                      >
                        <Cancel />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            {selectedPost && getPlatformIcon(selectedPost.platform)}
            <Typography variant="h6" sx={{ ml: 1 }}>
              Post Preview
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedPost && (
            <>
              <Typography variant="h6" gutterBottom>
                {selectedPost.contentType}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                {selectedPost.content?.text || selectedPost.content?.caption}
              </Typography>

              {selectedPost.content?.images && selectedPost.content?.images?.length > 0 && (
                selectedPost.contentType === 'carousel' ? (
                  <Box>
                    <Paper
                      square
                      elevation={0}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        height: 50,
                        pl: 2,
                        bgcolor: 'background.default',
                      }}
                    >
                      <Typography>
                        Slide {activeStep + 1} of {selectedPost.content?.images?.length || 0}
                      </Typography>
                    </Paper>
                    <Box sx={{ width: '100%', p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                      {selectedPost.content?.images?.[activeStep] ? (
                        <Box
                          component="iframe"
                          src={selectedPost.content?.images?.[activeStep]}
                          sx={{
                            width: '100%',
                            height: 400,
                            border: 'none',
                            borderRadius: 1,
                            bgcolor: 'white'
                          }}
                          title={`Slide ${activeStep + 1}`}
                        />
                      ) : (
                        <Typography color="text.secondary">Loading image...</Typography>
                      )}
                    </Box>
                    <MobileStepper
                      variant="text"
                      steps={selectedPost.content?.images?.length || 1}
                      position="static"
                      activeStep={activeStep}
                      nextButton={
                        <Button
                          size="small"
                          onClick={handleNext}
                          disabled={activeStep === (selectedPost.content?.images?.length || 1) - 1}
                        >
                          Next
                          <KeyboardArrowRight />
                        </Button>
                      }
                      backButton={
                        <Button
                          size="small"
                          onClick={handleBack}
                          disabled={activeStep === 0}
                        >
                          <KeyboardArrowLeft />
                          Back
                        </Button>
                      }
                    />
                  </Box>
                ) : (
                  <ImageList cols={2} gap={8}>
                    {(selectedPost.content?.images || []).map((image, index) => (
                      <ImageListItem key={index}>
                        <img
                          src={image}
                          alt={`Image ${index + 1}`}
                          loading="lazy"
                          style={{ maxWidth: '100%', height: 'auto' }}
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                )
              )}

              {selectedPost.content?.videoUrl && (
                <video
                  src={selectedPost.content?.videoUrl}
                  controls
                  style={{ width: '100%', maxHeight: '400px' }}
                />
              )}

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Scheduled for: {new Date(selectedPost.postDate).toLocaleString()}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          {selectedPost && (
            <>
              <Button
                color="primary"
                variant="contained"
                startIcon={<CheckCircle />}
                onClick={() => {
                  handleApprove(selectedPost.postId);
                  setPreviewOpen(false);
                }}
              >
                Approve
              </Button>
              <Button
                color="error"
                startIcon={<Cancel />}
                onClick={() => {
                  setPreviewOpen(false);
                  setRejectDialogOpen(true);
                }}
              >
                Reject
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Post</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason for rejection (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleReject} color="error" variant="contained">
            Reject Post
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PendingPosts;